import { DataType } from '@vgtv/api-client/lib/component';

import type { Component } from '@vgtv/api-client/lib/component';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { Profile } from '@vgtv/api-client/lib/cnp_tag';
import type { Page } from '@vgtv/api-client/lib/page';
import type { Vendor } from '../types';

export const isSvpAsset = (
  item: SvpAssetAdditional | SvpCategoryAdditional
): item is SvpAssetAdditional => {
  return 'assetType' in item;
};

export const isSvpCategory = (
  item: SvpAssetAdditional | SvpCategoryAdditional
): item is SvpCategoryAdditional => {
  return 'isSeries' in item;
};

function getComponentAssetsPreset(
  componentConfiguration: Component
): SvpAssetAdditional[] {
  if (
    (componentConfiguration.data?.type === DataType.ASSETS ||
      componentConfiguration.data?.type === DataType.STORIES) &&
    (componentConfiguration.data.preset ?? []).length > 0
  ) {
    return componentConfiguration.data.preset!;
  }

  if (
    componentConfiguration.data?.type === DataType.MIXED &&
    (componentConfiguration.data.preset ?? []).length > 0
  ) {
    return componentConfiguration.data.preset?.filter(isSvpAsset);
  }

  return [];
}

function getComponentCategoriesPreset(
  componentConfiguration: Component
): SvpCategoryAdditional[] {
  if (
    componentConfiguration.data?.type === DataType.CATEGORIES &&
    (componentConfiguration.data.preset ?? []).length > 0
  ) {
    return componentConfiguration.data.preset!;
  }

  if (
    componentConfiguration.data?.type === DataType.MIXED &&
    (componentConfiguration.data.preset ?? []).length > 0
  ) {
    return componentConfiguration.data.preset?.filter(isSvpCategory);
  }

  return [];
}

function getComponentProfilesPreset(
  componentConfiguration: Component
): Profile[] {
  if (
    componentConfiguration.data?.type === DataType.PROFILES &&
    (componentConfiguration.data.preset ?? []).length > 0
  ) {
    return componentConfiguration.data.preset!;
  }

  return [];
}

function getPageComponentsPresetAssets(
  pageConfiguration: Page
): SvpAssetAdditional[] {
  const { components = [] } = pageConfiguration;

  let assets: SvpAssetAdditional[] = [];

  components.forEach((component) => {
    const assetsPreset = getComponentAssetsPreset(component);

    assets = [...assets, ...assetsPreset];
  });

  return assets;
}

function getPageComponentsPresetCategories(
  pageConfiguration: Page
): Record<Vendor, SvpCategoryAdditional[]> {
  const { components = [] } = pageConfiguration;

  const categoriesMap: Record<Vendor, SvpCategoryAdditional[]> = {
    ab: [],
    vgtv: [],
    brandstudio: [],
  };

  components.forEach((component) => {
    const vendor = component.provider ?? pageConfiguration.provider;

    let categories = categoriesMap[vendor];

    const categoriesPreset = getComponentCategoriesPreset(component);
    const assetsPreset = getComponentAssetsPreset(component);

    categories = [...categories, ...categoriesPreset];

    const categoriesFromAssetsPreset = assetsPreset
      .map((asset) => asset.category)
      .filter((category) => Boolean(category))
      .filter((category) => !categories.find(({ id }) => id === category.id));

    categories = [...categories, ...categoriesFromAssetsPreset];

    categoriesMap[vendor] = categories;
  });

  return categoriesMap;
}

function getPageComponentsPresetProfiles(pageConfiguration: Page): Profile[] {
  const { components = [] } = pageConfiguration;

  let profiles: Profile[] = [];

  components.forEach((component) => {
    const profilesPreset = getComponentProfilesPreset(component);

    profiles = [...profiles, ...profilesPreset];
  });

  return profiles;
}

export {
  getPageComponentsPresetAssets,
  getPageComponentsPresetCategories,
  getPageComponentsPresetProfiles,
};
